import React, { useState } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import './Reviews.css';

function Reviews({ reviews = [] }) {
  const [currentPage, setCurrentPage] = useState(0);
  const [expandedReviews, setExpandedReviews] = useState(new Set());
  const reviewsPerPage = 5;

  if (!reviews || reviews.length === 0) {
    return null;
  }

  const totalPages = Math.ceil(reviews.length / reviewsPerPage);
  const startIndex = currentPage * reviewsPerPage;
  const visibleReviews = reviews.slice(startIndex, startIndex + reviewsPerPage);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <Star
        key={index}
        size={18}
        fill={index < rating ? "#FF0000" : "none"}
        stroke="#FF0000"
        strokeWidth={2}
      />
    ));
  };

  const toggleExpanded = (reviewId) => {
    const newExpanded = new Set(expandedReviews);
    if (newExpanded.has(reviewId)) {
      newExpanded.delete(reviewId);
    } else {
      newExpanded.add(reviewId);
    }
    setExpandedReviews(newExpanded);
  };

  const isLongReview = (text) => text && text.length > 200;

  const handlePrev = () => {
    setCurrentPage((prev) => (prev > 0 ? prev - 1 : totalPages - 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => (prev < totalPages - 1 ? prev + 1 : 0));
  };

  return (
    <section className="reviews-section" id="reviews">
      <div className="container">
        <h2 className="section-title">Client Success Stories</h2>
        <p className="section-subtitle">See what our clients have to say about their transformation journey</p>
        
        <div className="reviews-carousel-container">
          {reviews.length > reviewsPerPage && (
            <button className="carousel-nav prev" onClick={handlePrev} aria-label="Previous reviews">
              <ChevronLeft size={32} />
            </button>
          )}

          <div className="reviews-carousel">
            {visibleReviews.map((review) => {
              const isExpanded = expandedReviews.has(review.id);
              const needsReadMore = isLongReview(review.text);
              const truncatedText = needsReadMore && !isExpanded 
                ? review.text.substring(0, 200) + '...' 
                : review.text;

              return (
                <div 
                  key={review.id} 
                  className={`review-card ${isExpanded ? 'expanded' : ''}`}
                >
                  <div className="review-header">
                    <div className="review-avatar">
                      {review.imageUrl ? (
                        <img src={review.imageUrl} alt={review.name} />
                      ) : (
                        <div className="avatar-placeholder">
                          {review.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="review-info">
                      <h3 className="review-name">{review.name}</h3>
                      <div className="review-stars">
                        {renderStars(review.rating)}
                      </div>
                    </div>
                  </div>
                  
                  <p className="review-text">"{truncatedText}"</p>
                  
                  {needsReadMore && (
                    <button 
                      className="read-more-btn" 
                      onClick={() => toggleExpanded(review.id)}
                    >
                      {isExpanded ? 'Read Less' : 'Read More'}
                    </button>
                  )}
                  
                  {review.achievement && (
                    <div className="review-achievement">
                      <span className="achievement-badge">{review.achievement}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {reviews.length > reviewsPerPage && (
            <button className="carousel-nav next" onClick={handleNext} aria-label="Next reviews">
              <ChevronRight size={32} />
            </button>
          )}
        </div>

        {totalPages > 1 && (
          <div className="carousel-dots">
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentPage ? 'active' : ''}`}
                onClick={() => setCurrentPage(index)}
                aria-label={`Go to page ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default Reviews;
